import VectorImageLayer from 'ol/layer/VectorImage.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature.js';
import Point from 'ol/geom/Point.js';
import LineString from 'ol/geom/LineString.js';
import Polygon from 'ol/geom/Polygon.js';
import MultiPoint from 'ol/geom/MultiPoint.js';
import MultiLineString from 'ol/geom/MultiLineString.js';
import MultiPolygon from 'ol/geom/MultiPolygon.js';
import Stroke from 'ol/style/Stroke.js';
import Fill from 'ol/style/Fill.js';
import Style from 'ol/style/Style.js';
import { map } from './Map.js';

import { UILayerManager } from './Loaders/UILayerManager.js';
import { defaultStyle, getCADStyles, HIDDEN_STYLE } from './Loaders/StyleRegistry.js';
import { setBasemapOpacity } from './BasemapSwitcher.js';

/**
 * FileLoaderManager.js - Unified Data Import Router for GeoPST
 * Replaced the monolithic 1400 line legacy loader.
 */

let loadedLayers = [];
let uiManager;

$(document).ready(() => {
    uiManager = new UILayerManager(map, loadedLayers);

    const dropArea = document.getElementById('dropArea');
    const fileInput = document.getElementById('advancedFileInput');
    const topBarButton = document.getElementById('kmlUploadButton');

    // Basemap Transparency Control
    const transSection = document.getElementById('basemapTransparencySection');
    const transSlider = document.getElementById('basemapOpacitySlider');
    const transValue = document.getElementById('basemapOpacityValue');

    if (transSlider && transValue) {
        transSlider.addEventListener('input', (e) => {
            const val = e.target.value;
            transValue.textContent = `${val}%`;
            setBasemapOpacity(parseFloat(val) / 100);
        });
    }

    const updateBasemapTransparencyUI = () => {
        if (!transSection) return;
        if (loadedLayers.length > 0) {
            transSection.classList.remove('hidden');
        } else {
            transSection.classList.add('hidden');
            // Reset opacity if all layers are removed
            setBasemapOpacity(1);
            if (transSlider) transSlider.value = 100;
            if (transValue) transValue.textContent = '100%';
        }
    };

    // Expose for internal updates
    window.refreshBasemapTransparencyUI = updateBasemapTransparencyUI;

    if (topBarButton && fileInput) {
        topBarButton.addEventListener('click', () => fileInput.click());
    }

    if (dropArea && fileInput) {
        dropArea.addEventListener('dragover', (e) => {
            e.preventDefault();
            dropArea.classList.add('active');
        });

        dropArea.addEventListener('dragleave', () => dropArea.classList.remove('active'));

        dropArea.addEventListener('drop', (e) => {
            e.preventDefault();
            dropArea.classList.remove('active');
            handleFiles(e.dataTransfer.files);
        });

        fileInput.addEventListener('change', (e) => {
            handleFiles(e.target.files);
            e.target.value = '';
        });
    }
});

async function handleFiles(files) {
    for (const file of files) {
        const name = file.name.toLowerCase();
        try {
            window.showLoading?.();

            // Allow DOM to update the loading screen first
            await new Promise(r => setTimeout(r, 50));

            let result = null;
            const mapProjection = map.getView().getProjection();

            if (name.endsWith('.kml')) {
                const { parseKMLText } = await import('./Loaders/KMLEngine.js');
                const text = await file.text();
                result = parseKMLText(text, mapProjection);
            } else if (name.endsWith('.kmz')) {
                const { parseKMLText, readKmzAndInlineIcons } = await import('./Loaders/KMLEngine.js');
                const buffer = await file.arrayBuffer();
                const kmlText = await readKmzAndInlineIcons(buffer);
                result = parseKMLText(kmlText, mapProjection);
            } else if (name.endsWith('.zip')) {
                const { loadShapefileAsync } = await import('./Loaders/StandardLoaders.js');
                const buffer = await file.arrayBuffer();
                result = await loadShapefileAsync(buffer, mapProjection);
            } else if (name.endsWith('.xlsx') || name.endsWith('.xls') || name.endsWith('.csv')) {
                const { loadSpreadsheetAsync } = await import('./Loaders/StandardLoaders.js');
                const buffer = await file.arrayBuffer();
                result = await loadSpreadsheetAsync(buffer, mapProjection);
            } else if (name.endsWith('.dxf')) {
                const text = await file.text();
                result = await new Promise((resolve, reject) => {
                    const layerId = `Import_${Date.now()}`;
                    addFeaturesToMap([], file.name, null, null, layerId); // Initial empty card

                    const worker = new Worker(new URL('./Loaders/Workers/DXFWorker.js', import.meta.url), { type: 'module' });
                    worker.postMessage({ text });

                    worker.onmessage = (e) => {
                        if (e.data.success) {
                            if (e.data.isInitial) {
                                // Update tree when it finally arrives
                                const layerObj = loadedLayers.find(l => l.name === layerId);
                                if (layerObj) layerObj.tree = e.data.tree;

                                if (e.data.isDone) {
                                    handleWorkerChunk(null, layerId, true, () => {
                                        worker.terminate();
                                        resolve(null);
                                    });
                                }
                            } else {
                                handleWorkerChunk(e.data.consolidated, layerId, e.data.isDone, () => {
                                    if (e.data.isDone) {
                                        worker.terminate();
                                        resolve(null);
                                    }
                                });
                            }
                        } else {
                            reject(new Error(e.data.error));
                            worker.terminate();
                        }
                    };
                });
            } else if (name.endsWith('.dwg')) {
                const buffer = await file.arrayBuffer();
                result = await new Promise((resolve, reject) => {
                    const layerId = `Import_${Date.now()}`;
                    addFeaturesToMap([], file.name, null, null, layerId); // Initial empty card

                    const worker = new Worker(new URL('./Loaders/Workers/DWGWorker.js', import.meta.url), { type: 'module' });
                    worker.postMessage({ buffer }, [buffer]);

                    worker.onmessage = (e) => {
                        if (e.data.success) {
                            if (e.data.isInitial) {
                                // Update tree when it finally arrives
                                const layerObj = loadedLayers.find(l => l.name === layerId);
                                if (layerObj) layerObj.tree = e.data.tree;

                                if (e.data.isDone) {
                                    handleWorkerChunk(null, layerId, true, () => {
                                        worker.terminate();
                                        resolve(null);
                                    });
                                }
                            } else {
                                handleWorkerChunk(e.data.consolidated, layerId, e.data.isDone, () => {
                                    if (e.data.isDone) {
                                        worker.terminate();
                                        resolve(null);
                                    }
                                });
                            }
                        } else {
                            reject(new Error(e.data.error));
                            worker.terminate();
                        }
                    };
                });
            } else {
                window.showErrorModal?.(`الملف ${file.name} غير مدعوم حالياً.`);
                continue;
            }

            if (result && result.features && result.features.length > 0) {
                addFeaturesToMap(result.features, file.name, null, result.tree);
            }

        } catch (err) {
            console.error('File load error:', err);
            window.showErrorModal?.(err.message || `فشل تحميل الملف: ${file.name}`);
            window.hideLoading?.(); // Hide on error
        } finally {
            // Only hide loading if it's NOT a worker file (worker handles its own hideLoading)
            if (!name.endsWith('.dwg') && !name.endsWith('.dxf')) {
                window.hideLoading?.();
            }
        }
    }
}

function addFeaturesToMap(features, fileName, customStyle = null, tree = null, existingLayerId = null) {
    const layerId = existingLayerId || `Import_${Date.now()}`;

    // Separate Text from Geometries for Hybrid Rendering
    const textFeatures = [];
    const geomFeatures = [];

    features.forEach(f => {
        f.set('layerId', layerId);
        const type = f.get('type');
        const geom = f.getGeometry();
        if (!geom) return;
        const geomType = geom.getType();
        const text = f.get('text');

        if (text || type === 'TEXT' || type === 'MTEXT' || type === 'INSERT') {
            textFeatures.push(f);
        } else {
            geomFeatures.push(f);
        }
    });

    const textSource = new VectorSource({ features: textFeatures });
    const geomSource = new VectorSource({ features: geomFeatures });

    // 1. Geometry Layer (Performance & Stability)
    const geomLayer = new VectorImageLayer({
        id: `${layerId}_geom`,
        source: geomSource,
        zIndex: 9,
        imageRatio: 1.5,
        renderBuffer: 100,
        renderOrder: function(f1, f2) {
            const z1 = f1.get('cadZIndex') || 0;
            const z2 = f2.get('cadZIndex') || 0;
            return z1 - z2;
        },
        style: function (feature, resolution) {
            if (feature.get('isVisible') === 0) return HIDDEN_STYLE;
            const color = feature.get('entityColor') || '#000000';
            const styles = getCADStyles(color);
            if (!styles) return null;

            const geomType = feature.getGeometry().getType();
            if (geomType === 'Polygon' || geomType === 'MultiPolygon') {
                return resolution > 2.0 ? styles.polySimpleStyle : styles.polyStyle;
            } else if (geomType === 'Point' || geomType === 'MultiPoint') {
                return resolution > 2.0 ? styles.pointSimpleStyle : styles.pointStyle;
            } else {
                return resolution > 2.0 ? styles.lineSimpleStyle : styles.lineStyle;
            }
        }
    });

    // 2. Text Layer (Golden Solution for Mobile 60fps)
    const defaultTextRes = parseFloat(localStorage.getItem('geopst_text_density')) || 0.45;
    
    const textLayer = new VectorLayer({
        id: `${layerId}_text`,
        source: textSource,
        zIndex: 11,
        declutter: true, 
        maxResolution: defaultTextRes, 
        minResolution: 0.005,
        renderBuffer: 40,
        renderOrder: function(f1, f2) {
            const z1 = f1.get('cadZIndex') || 0;
            const z2 = f2.get('cadZIndex') || 0;
            return z1 - z2;
        },
        updateWhileInteracting: false,
        updateWhileAnimating: false
    });

    import('./Loaders/StyleRegistry.js').then(({ getCADTextStyle, HIDDEN_STYLE }) => {
        textLayer.setStyle((feature) => {
            if (feature.get('isVisible') === 0) return HIDDEN_STYLE;
            return getCADTextStyle(feature.get('text'), feature.get('entityColor') || '#000000');
        });
    });
    // Sync visibility between them
    const syncVisibility = () => {
        const vis = geomLayer.getVisible();
        if (textLayer.getVisible() !== vis) textLayer.setVisible(vis);
    };
    geomLayer.on('change:visible', syncVisibility);

    geomLayer.set('name', layerId);
    geomLayer.set('title', fileName);

    // Hide others
    if (window.hideAllLayers) {
        window.hideAllLayers();
    }

    geomLayer.on('change:visible', function () {
        const checkbox = document.getElementById(`viz-toggle-${layerId}`);
        if (checkbox) {
            checkbox.checked = geomLayer.getVisible();
        }
    });

    loadedLayers.forEach(l => {
        if (l.layer) l.layer.setVisible(false);
        if (l.geomLayer) l.geomLayer.setVisible(false);
        if (l.textLayer) l.textLayer.setVisible(false);
        const checkbox = document.getElementById(`viz-toggle-${l.name}`);
        if (checkbox) checkbox.checked = false;
    });

    map.addLayer(geomLayer);
    map.addLayer(textLayer);

    // Build folder index for KML/others that don't use workers
    const folderIndex = {};
    features.forEach(f => {
        const folder = f.get('cadFolder') || '__loose__';
        if (!folderIndex[folder]) folderIndex[folder] = [];
        folderIndex[folder].push(f);
    });

    loadedLayers.push({
        name: layerId,
        layer: geomLayer, // Proxy for UI
        geomLayer,
        textLayer,
        source: geomSource,
        textSource,
        geomSource,
        tree,
        folderIndex
    });


    const extent = geomSource.getExtent();
    if (extent && extent[0] !== Infinity) {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 1000, maxZoom: 11 });
    }

    addLayerToUI(layerId, fileName, features.length, tree);
    if (window.refreshBasemapTransparencyUI) window.refreshBasemapTransparencyUI();
}

function handleWorkerChunk(consolidated, layerId, isDone, callback) {
    let layerObj = loadedLayers.find(l => l.name === layerId);
    if (!layerObj) return;

    layerObj.chunkQueue = layerObj.chunkQueue || [];
    layerObj.tempGeomFeats = layerObj.tempGeomFeats || [];
    layerObj.tempTextFeats = layerObj.tempTextFeats || [];
    layerObj.isWorkerDone = layerObj.isWorkerDone || isDone;
    if (callback) layerObj.finalCallback = callback;

    if (consolidated) {
        Object.keys(consolidated).forEach(ln => {
            Object.keys(consolidated[ln]).forEach(key => {
                const d = consolidated[ln][key];
                if (d.polygons.length) layerObj.chunkQueue.push({ type: 'poly', data: d.polygons, layer: ln, color: d.color });
                if (d.lines.length) layerObj.chunkQueue.push({ type: 'line', data: d.lines, layer: ln, color: d.color });
                if (d.points.length) layerObj.chunkQueue.push({ type: 'point', data: d.points, layer: ln, color: d.color });
                for (let t = 0; t < d.texts.length; t += 100) {
                    layerObj.chunkQueue.push({ type: 'texts', data: d.texts.slice(t, t + 100), layer: ln, color: d.color });
                }
            });
        });
    }

    if (!layerObj.isProcessing) {
        layerObj.isProcessing = true;
        import('./Loaders/StyleRegistry.js').then(({ getCADTextStyle, HIDDEN_STYLE, colorAlpha }) => {
            function processQueue() {
                const tasks = layerObj.chunkQueue;
                if (tasks.length === 0) {
                    layerObj.isProcessing = false;
                    if (layerObj.isWorkerDone) finalizeLayer(layerId, layerObj.finalCallback);
                    return;
                }

                // Initialize spatial folder index if it doesn't exist
                layerObj.folderIndex = layerObj.folderIndex || {};

                const batchSize = Math.min(25, tasks.length);
                let addedCount = 0;

                for (let i = 0; i < batchSize; i++) {
                    const task = tasks.shift();
                    const { layer, color } = task;

                    const addFeatureToIndex = (f) => {
                        const folder = f.get('cadFolder');
                        if (!layerObj.folderIndex[folder]) layerObj.folderIndex[folder] = [];
                        layerObj.folderIndex[folder].push(f);
                        addedCount++;
                    };

                    if (task.type === 'poly') {
                        const f = new Feature({ geometry: new MultiPolygon(task.data) });
                        f.setProperties({ layer, cadFolder: layer, isVisible: 1, entityColor: color, polyFillColor: colorAlpha(color, 0.4) });
                        layerObj.tempGeomFeats.push(f);
                        addFeatureToIndex(f);
                    } else if (task.type === 'line') {
                        const trueLines = [];
                        const truePolys = [];
                        
                        task.data.forEach(lineCoords => {
                            let isMissingClose = false;
                            
                            // 1. Auto-detect unclosed 4-point rectangles (3 segments)
                            if (lineCoords.length === 4) {
                                const dist = (p1, p2) => Math.hypot(p2[0]-p1[0], p2[1]-p1[1]);
                                const L1 = dist(lineCoords[0], lineCoords[1]);
                                const W1 = dist(lineCoords[1], lineCoords[2]);
                                const L2 = dist(lineCoords[2], lineCoords[3]);
                                const W2 = dist(lineCoords[3], lineCoords[0]); 
                                const diag1 = dist(lineCoords[0], lineCoords[2]);
                                const diag2 = dist(lineCoords[1], lineCoords[3]);

                                const maxL = Math.max(L1, L2, 1e-6);
                                const maxW = Math.max(W1, W2, 1e-6);
                                const maxDiag = Math.max(diag1, diag2, 1e-6);

                                // 20% tolerance for CAD drawing inaccuracies
                                if (Math.abs(L1 - L2) / maxL < 0.20 && Math.abs(W1 - W2) / maxW < 0.20 && Math.abs(diag1 - diag2) / maxDiag < 0.20) {
                                    isMissingClose = true;
                                }
                            }
                            
                            // 2. Auto-detect manually closed shapes that erroneously ended up in lines array
                            if (lineCoords.length > 3 && Math.hypot(lineCoords[0][0] - lineCoords[lineCoords.length-1][0], lineCoords[0][1] - lineCoords[lineCoords.length-1][1]) < 0.05) {
                                isMissingClose = true;
                            }

                            if (isMissingClose) {
                                const closedCoords = [...lineCoords];
                                // Force close it mathematically
                                if (Math.hypot(closedCoords[0][0] - closedCoords[closedCoords.length-1][0], closedCoords[0][1] - closedCoords[closedCoords.length-1][1]) > 1e-6) {
                                    closedCoords.push([closedCoords[0][0], closedCoords[0][1]]);
                                }
                                truePolys.push([closedCoords]); // Wrap in array for LinearRing
                            } else {
                                trueLines.push(lineCoords);
                            }
                        });

                        if (trueLines.length > 0) {
                            const f = new Feature({ geometry: new MultiLineString(trueLines) });
                            f.setProperties({ layer, cadFolder: layer, isVisible: 1, entityColor: color, polyFillColor: 'rgba(0,0,0,0)' });
                            layerObj.tempGeomFeats.push(f);
                            addFeatureToIndex(f);
                        }
                        
                        if (truePolys.length > 0) {
                            const f = new Feature({ geometry: new MultiPolygon(truePolys) });
                            f.setProperties({ layer, cadFolder: layer, isVisible: 1, entityColor: color, polyFillColor: colorAlpha(color, 0.4) });
                            layerObj.tempGeomFeats.push(f);
                            addFeatureToIndex(f);
                        }
                    } else if (task.type === 'point') {
                        const f = new Feature({ geometry: new MultiPoint(task.data) });
                        f.setProperties({ layer, cadFolder: layer, isVisible: 1, entityColor: color, polyFillColor: 'rgba(0,0,0,0)' });
                        layerObj.tempGeomFeats.push(f);
                        addFeatureToIndex(f);
                    } else if (task.type === 'texts') {
                        task.data.forEach(item => {
                            const f = new Feature({ geometry: new Point(item.coords) });
                            f.setProperties({ layer, type: 'TEXT', cadFolder: layer, isVisible: 1, text: item.text, entityColor: color });
                            layerObj.tempTextFeats.push(f);
                            addFeatureToIndex(f);
                        });
                    }
                }

                // Throttle DOM updates (only update if queue length is divisible by 5, or near end)
                if (tasks.length % 5 === 0 || tasks.length < 5) {
                    const countEl = document.querySelector(`#card-${layerId} .imported-layer-count`);
                    if (countEl) {
                        countEl.textContent = `جاري التحميل... ${layerObj.tempGeomFeats.length + layerObj.tempTextFeats.length}`;
                    }
                }

                requestAnimationFrame(processQueue);
            }
            requestAnimationFrame(processQueue);
        });
    }
}

function finalizeLayer(layerId, callback) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (!layerObj) { if (callback) callback(); return; }

    // CRITICAL: Bulk insert all features at once to build the R-Tree efficiently
    if (layerObj.tempGeomFeats && layerObj.tempGeomFeats.length > 0 && layerObj.geomSource) {
        layerObj.geomSource.addFeatures(layerObj.tempGeomFeats);
    }
    if (layerObj.tempTextFeats && layerObj.tempTextFeats.length > 0 && layerObj.textSource) {
        layerObj.textSource.addFeatures(layerObj.tempTextFeats);
    }

    const countEl = document.querySelector(`#card-${layerId} .imported-layer-count`);
    if (countEl && layerObj.geomSource && layerObj.textSource) {
        const finalTotal = layerObj.geomSource.getFeatures().length + layerObj.textSource.getFeatures().length;
        countEl.textContent = `${finalTotal} عنصر`;
    }

    const card = document.getElementById(`card-${layerId}`);
    if (card && layerObj.tree) {
        const treeSection = card.querySelector('.fl-tree-section');
        if (!treeSection && Object.keys(layerObj.tree.children).length > 0) {
            const panel = card.querySelector('.imported-settings-panel');
            const treeHtml = `
                <div class="fl-tree-section">
                    <div class="fl-tree-header"><i class="fas fa-folder-tree"></i> <span>محتويات الملف</span></div>
                    <div class="fl-tree-body">${renderTreeNode(layerObj.tree, layerId, '')}</div>
                </div>
            `;
            const actions = card.querySelector('.imported-layer-actions');
            const div = document.createElement('div');
            div.innerHTML = treeHtml;
            panel.insertBefore(div.firstElementChild, actions);
        }
    }

    const extent = layerObj.geomSource.getExtent();
    if (extent && extent[0] !== Infinity && extent[0] !== -Infinity) {
        map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
    }

    // Clean up temporary memory arrays immediately to prevent RAM bloat
    delete layerObj.chunkQueue;
    delete layerObj.tempGeomFeats;
    delete layerObj.tempTextFeats;

    window.hideLoading?.();
    if (callback) callback();
}

function addLayerToUI(layerId, title, count, tree = null) {
    const list = document.getElementById('loadedFilesList');
    if (!list) return;

    let treeHtml = '';
    const hasTree = tree && Object.keys(tree.children).length > 0;

    if (hasTree) {
        treeHtml = `
            <div class="fl-tree-section">
                    <div class="fl-tree-header">
                        <i class="fas fa-list-ul"></i>
                        <span>محتويات الملف</span>
                    </div>
                <div class="fl-tree-body">
                    ${renderTreeNode(tree, layerId, '')}
                </div>
            </div>
        `;
    }

    const card = document.createElement('div');
    card.className = 'imported-layer-card';
    card.id = `card-${layerId}`;
    card.innerHTML = `
        <div class="imported-layer-header">
            <label class="imported-viz-toggle" onclick="event.stopPropagation()">
                <input type="checkbox" id="viz-toggle-${layerId}" checked onchange="toggleImportedVisibility('${layerId}', this.checked)">
                <span class="imported-viz-slider"></span>
            </label>
            <div class="imported-layer-info" onclick="toggleImportedSettings('${layerId}')">
                <span class="imported-layer-title" title="${title}">${title}</span>
                <span class="imported-layer-count">${count} عنصر</span>
            </div>
            <i class="fas fa-chevron-down imported-chevron" onclick="toggleImportedSettings('${layerId}')"></i>
        </div>

        <div class="imported-settings-panel" id="settings-${layerId}">
            <div class="imported-setting-row">
                <div class="imported-setting-label">
                    <i class="fas fa-adjust"></i>
                    <span>الشفافية</span>
                </div>
                <div class="imported-setting-control">
                    <input type="range" class="imported-opacity-slider" min="0" max="100" value="100" 
                        oninput="updateImportedOpacity('${layerId}', this.value)">
                    <span class="imported-opacity-value" id="opacity-val-${layerId}">100%</span>
                </div>
            </div>

            ${treeHtml}

            <div class="imported-setting-row">
                <div class="imported-setting-label">
                    <i class="fas fa-search"></i>
                    <span>كثافة النصوص</span>
                </div>
                <div class="imported-setting-control">
                    <input type="range" class="imported-opacity-slider" min="1" max="100" value="50" 
                        id="density-slider-${layerId}" oninput="updateTextDensity('${layerId}', this.value)">
                </div>
            </div>

            <div class="imported-layer-actions">
                <button class="imported-action-btn labels active-btn" id="lbl-btn-${layerId}" onclick="toggleCADLabels('${layerId}', this)">
                    <i class="fas fa-font"></i> إخفاء النصوص
                </button>
                <button class="imported-action-btn zoom" onclick="zoomToImported('${layerId}')">
                    <i class="fas fa-search-plus"></i> تقريب
                </button>
                <button class="imported-action-btn delete" onclick="removeImported('${layerId}')">
                    <i class="fas fa-trash-alt"></i> حذف
                </button>
            </div>
        </div>
    `;
    // تأكد من أن الـ slider يأخذ القيمة المحفوظة حتى لو تأخر الـ DOM
    setTimeout(() => {
        const densitySlider = document.getElementById(`density-slider-${layerId}`);
        if (densitySlider) {
            const savedRes = parseFloat(localStorage.getItem('geopst_text_density')) || 0.45;
            const percent = Math.round(((savedRes - 0.1) / 1.4) * 100);
            densitySlider.value = Math.max(1, Math.min(100, percent));
        }
    }, 10);

    list.prepend(card);

    // Sync density slider with saved preference for the newly created layer
    const savedRes = parseFloat(localStorage.getItem('geopst_text_density')) || 0.45;
    const minRes = 0.1, maxRes = 1.5;
    const sliderPercent = Math.round(((savedRes - minRes) / (maxRes - minRes)) * 100);
    const densitySlider = document.getElementById(`density-slider-${layerId}`);
    if (densitySlider) {
        densitySlider.value = Math.max(1, Math.min(100, sliderPercent));
    }
}

function renderTreeNode(node, layerId, pathPrefix) {
    let html = '';
    const children = Object.entries(node.children);
    if (children.length === 0) return '';

    for (const [name, child] of children) {
        const fullPath = pathPrefix ? `${pathPrefix} / ${name}` : name;
        const escapedPath = fullPath.replace(/'/g, "\\'").replace(/"/g, '&quot;');
        const hasChildren = Object.keys(child.children).length > 0;
        const totalCount = countAllFeatures(child);

        if (hasChildren) {
            html += `
            <div class="fl-tree-group">
                <div class="fl-tree-group-header" onclick="toggleTreeGroup(this)">
                    <i class="fas fa-chevron-left fl-tree-arrow"></i>
                    <input type="checkbox" checked onclick="event.stopPropagation(); toggleTreeFolder('${layerId}', '${escapedPath}', this.checked)">
                    <i class="fas fa-folder fl-tree-folder-icon"></i>
                    <span class="fl-tree-name" title="${name}">${name}</span>
                    <span class="fl-tree-count">${totalCount}</span>
                </div>
                <div class="fl-tree-children">
                    ${child.count > 0 && Object.keys(child.children).length > 0 ? '' : ''}
                    ${renderTreeNode(child, layerId, fullPath)}
                </div>
            </div>
            `;
        } else {
            html += `
            <div class="fl-tree-leaf">
                <input type="checkbox" checked onclick="event.stopPropagation(); toggleTreeFolder('${layerId}', '${escapedPath}', this.checked)">
                <i class="fas fa-layer-group fl-tree-leaf-icon"></i>
                <span class="fl-tree-name" title="${name}">${name}</span>
                <span class="fl-tree-count">${child.count}</span>
            </div>
            `;
        }
    }
    return html;
}

function countAllFeatures(node) {
    let total = node.count || 0;
    for (const child of Object.values(node.children)) {
        total += countAllFeatures(child);
    }
    return total;
}

// UI Event Handlers exposed to window
window.toggleTreeGroup = function (headerEl) {
    const group = headerEl.closest('.fl-tree-group');
    if (!group) return;
    group.classList.toggle('collapsed');
};

window.toggleTreeFolder = function (layerId, folderPath, visible) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (!layerObj || !layerObj.folderIndex) return;

    // Use the pre-built index to instantly find features belonging to this folder or its subfolders
    const targetKeys = Object.keys(layerObj.folderIndex).filter(key =>
        key === folderPath || key.startsWith(folderPath + ' / ')
    );

    targetKeys.forEach(key => {
        const features = layerObj.folderIndex[key];
        features.forEach(f => f.set('isVisible', visible ? 1 : 0));
    });

    layerObj.geomLayer?.changed();
    layerObj.textLayer?.changed();

    const card = document.getElementById(`card-${layerId}`);
    if (!card) return;
    const allHeaders = card.querySelectorAll('.fl-tree-group-header, .fl-tree-leaf');
    allHeaders.forEach(h => {
        const cb = h.querySelector('input[type="checkbox"]');
        if (!cb) return;
        const onclick = cb.getAttribute('onclick') || '';
        if (onclick.includes(folderPath + ' / ') || onclick.includes("'" + folderPath + "'")) {
            if (onclick.includes(folderPath + ' / ')) {
                cb.checked = visible;
            }
        }
    });
};

window.toggleImportedSettings = function (layerId) {
    const panel = document.getElementById(`settings-${layerId}`);
    const card = document.getElementById(`card-${layerId}`);
    if (!panel || !card) return;

    const isVisible = panel.classList.contains('open');

    document.querySelectorAll('.imported-settings-panel.open').forEach(p => {
        p.classList.remove('open');
        p.closest('.imported-layer-card')?.querySelector('.imported-chevron')?.classList.remove('rotated');
    });

    if (!isVisible) {
        panel.classList.add('open');
        card.querySelector('.imported-chevron')?.classList.add('rotated');
    }
};

window.toggleImportedVisibility = function (layerId, visible) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (layerObj) {
        if (layerObj.geomLayer) layerObj.geomLayer.setVisible(visible);
        if (layerObj.textLayer) layerObj.textLayer.setVisible(visible);
        if (window.refreshSnapping) window.refreshSnapping();
    }
};

window.updateImportedOpacity = function (layerId, value) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (layerObj) {
        const opacity = parseFloat(value) / 100;
        if (layerObj.geomLayer) layerObj.geomLayer.setOpacity(opacity);
        if (layerObj.textLayer) layerObj.textLayer.setOpacity(opacity);
        const valEl = document.getElementById(`opacity-val-${layerId}`);
        if (valEl) valEl.textContent = `${value}%`;
    }
};

window.zoomToImported = function (layerId) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (layerObj) {
        const extent = layerObj.geomSource?.getExtent();
        if (extent && extent[0] !== Infinity) {
            map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
        }
    }
};

window.removeImported = function (layerId) {
    const idx = loadedLayers.findIndex(l => l.name === layerId);
    if (idx === -1) return;
    const obj = loadedLayers[idx];

    if (obj.geomSource) obj.geomSource.clear();
    if (obj.textSource) obj.textSource.clear();
    if (obj.geomLayer) { map.removeLayer(obj.geomLayer); obj.geomLayer.dispose?.(); }
    if (obj.textLayer) { map.removeLayer(obj.textLayer); obj.textLayer.dispose?.(); }

    loadedLayers.splice(idx, 1);
    const card = document.getElementById(`card-${layerId}`);
    if (card) {
        card.style.animation = 'fadeOutScale 0.3s ease forwards';
        setTimeout(() => {
            card.remove();
            if (window.refreshBasemapTransparencyUI) window.refreshBasemapTransparencyUI();
        }, 300);
    }
};

window.toggleCADLabels = function(layerId, btn) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (!layerObj) return;

    let nowVisible = true;
    if (layerObj.textLayer) {
        nowVisible = !layerObj.textLayer.getVisible();
        layerObj.textLayer.setVisible(nowVisible);
    } else {
        nowVisible = !btn.classList.contains('active-btn');
    }

    // Set global flag for KML custom styles
    window.layerLabelVisibility = window.layerLabelVisibility || {};
    window.layerLabelVisibility[layerId] = nowVisible;

    // Force redraw of geomLayer for KML to apply style changes
    if (layerObj.geomLayer) {
        layerObj.geomLayer.changed();
    }

    // Toggle a standard class for active/inactive state to respect CSS theme
    if (nowVisible) {
        btn.classList.add('active-btn');
        btn.innerHTML = `<i class="fas fa-font"></i> إخفاء النصوص`;
    } else {
        btn.classList.remove('active-btn');
        btn.innerHTML = `<i class="fas fa-font"></i> إظهار النصوص`;
    }
};

window.updateTextDensity = function(layerId, sliderValue) {
    const layerObj = loadedLayers.find(l => l.name === layerId);
    if (!layerObj || !layerObj.textLayer) return;

    // Convert 1-100 slider to maxResolution (e.g., 0.1 to 1.5)
    // Higher slider value = see text from further away = higher maxResolution
    const minRes = 0.1;
    const maxRes = 1.5;
    const mappedResolution = minRes + ((sliderValue / 100) * (maxRes - minRes));
    
    layerObj.textLayer.setMaxResolution(mappedResolution);
    layerObj.textLayer.changed(); // Force re-render

    // Save user preference
    localStorage.setItem('geopst_text_density', mappedResolution.toString());
};

// On load, sync slider UI with saved localStorage
document.addEventListener('DOMContentLoaded', () => {
    const savedRes = parseFloat(localStorage.getItem('geopst_text_density'));
    if (savedRes) {
        const minRes = 0.1;
        const maxRes = 1.5;
        const sliderVal = ((savedRes - minRes) / (maxRes - minRes)) * 100;
        // The script will map this to any dynamically created slider
        window.defaultDensitySliderValue = Math.round(sliderVal);
    }
});

export { loadedLayers };
